import { useState } from 'react';

interface Props {
  stageName: string;
  /** Opens the first shooter; `shuffle` randomises the round first. */
  onPicked: (shuffle: boolean) => Promise<void>;
  /** Round already exists — leave it as-is and go to the roster. */
  onBack: () => void;
}

/**
 * Last step before a new round's first shooter opens: keep the default
 * (squad) order or shuffle it, so the same shooter isn't always first.
 */
export function OrderPicker({ stageName, onPicked, onBack }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function pick(shuffle: boolean) {
    setBusy(true);
    setError(null);
    try {
      await onPicked(shuffle);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <section className="space-y-4">
      <header className="flex items-center justify-between">
        <button
          onClick={onBack}
          disabled={busy}
          className="min-h-12 pr-3 text-sm text-zinc-500 underline"
        >
          ← zur Liste
        </button>
        <h2 className="text-lg font-bold">Reihenfolge wählen</h2>
      </header>

      <p className="text-sm text-zinc-400">
        <span className="font-medium text-zinc-200">{stageName}</span>
      </p>

      {error && <p className="rounded bg-red-900/30 p-3 text-sm text-red-300">Fehler: {error}</p>}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <button
          onClick={() => void pick(false)}
          disabled={busy}
          className="flex flex-col items-start gap-2 rounded-xl border border-zinc-700 bg-zinc-900 px-5 py-6 text-left transition-colors hover:border-emerald-500 hover:bg-zinc-800 disabled:opacity-50"
        >
          <span className="text-3xl">📋</span>
          <span className="text-2xl font-bold">Standard</span>
          <span className="text-sm text-zinc-400">Reihenfolge laut Startliste.</span>
        </button>

        <button
          onClick={() => void pick(true)}
          disabled={busy}
          className="flex flex-col items-start gap-2 rounded-xl border border-zinc-700 bg-zinc-900 px-5 py-6 text-left transition-colors hover:border-amber-500 hover:bg-zinc-800 disabled:opacity-50"
        >
          <span className="text-3xl">🔀</span>
          <span className="text-2xl font-bold">Mischen</span>
          <span className="text-sm text-zinc-400">Zufällige Reihenfolge für diese Runde.</span>
        </button>
      </div>
    </section>
  );
}
